import React, { useState, useEffect } from 'react'
import { ImageBackground, View, TouchableOpacity, Animated, Platform, Alert, Linking } from 'react-native'
import { useNavigation, useTheme } from '@react-navigation/native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { Button, Screen, Text, Icon, OnBoarding } from '@components'
import { GenericNavigationProp, ScreenAuthProps } from '@navigation'
import { finalStepRegister } from '@assets'
import { useLanguage } from '@context'
import { useAnimatedValues, useGetProfile } from '@hooks'
import getStyle from './style'
import { useCurrentUserStore } from '@store'
import { getCurrentPosition } from '@utils'

export const FinalStep: React.FC<ScreenAuthProps<'FinalStep'>> = ({ route, navigation }) => {
  const { navigate } = useNavigation<GenericNavigationProp>()

  const { t } = useLanguage()
  const { top } = useSafeAreaInsets()

  const theme = useTheme()
  const styles = getStyle(theme, top)

  const { data: profile } = useGetProfile()
  const { setUserID } = useCurrentUserStore()

  const [isGeolocationEnabled, setIsGeolocationEnabled] = useState(false)
  const { animatedValues, registerAnimation } = useAnimatedValues([0, 0]) // [scale, opacity]
  const [scaleAnim, opacityAnim] = animatedValues

  useEffect(() => {
    if (isGeolocationEnabled) {
      const animations = Animated.parallel([
        registerAnimation(Animated.spring(scaleAnim, {
          toValue: 1,
          useNativeDriver: true,
          tension: 150,
          friction: 4,
        })),
        registerAnimation(Animated.timing(opacityAnim, {
          toValue: 1,
          duration: 200,
          useNativeDriver: true,
        })),
      ])
      animations.start()
    } else {
      const animations = Animated.parallel([
        registerAnimation(Animated.timing(scaleAnim, {
          toValue: 0,
          duration: 150,
          useNativeDriver: true,
        })),
        registerAnimation(Animated.timing(opacityAnim, {
          toValue: 0,
          duration: 150,
          useNativeDriver: true,
        })),
      ])
      animations.start()
    }
  }, [isGeolocationEnabled, scaleAnim, opacityAnim, registerAnimation])

  /**
   * Web: navigator.geolocation prompts the browser; react-native-permissions does not support web.
   * Native: expo-location (via getCurrentPosition) requests foreground permission — same stack as the rest of the app.
   */
  const requestLocationPermission = async () => {
    try {
      const coords = await getCurrentPosition()
      if (coords) {
        setIsGeolocationEnabled(true)
        return
      }

      setIsGeolocationEnabled(false)

      if (Platform.OS === 'web') {
        return
      }

      Alert.alert(
        t('geolocation.denied.title'),
        t('geolocation.denied.message'),
        [
          {
            text: t('geolocation.denied.cancel'),
            style: 'cancel',
          },
          {
            text: t('geolocation.denied.settings'),
            onPress: () => Linking.openSettings?.(),
          },
        ],
      )
    } catch (error) {
      console.error('Erreur lors de la demande de permission de géolocalisation:', error)
      setIsGeolocationEnabled(false)
    }
  }

  const handleGeolocationToggle = () => {
    if (!isGeolocationEnabled) {
      requestLocationPermission()
    } else {
      setIsGeolocationEnabled(false)
    }
  }

  return (
    <Screen noPaddingTop noPaddingBottom>
      <ImageBackground source={finalStepRegister} style={styles.finalContainer}>
        <OnBoarding>
          <View style={styles.finalTitleContainer}>
            <Text type="headline" color={theme.colors.white} style={styles.finalTitle}>
              {t('registerFinalStep.congrats', { name: profile?.firstName, surname: profile?.lastName })}
            </Text>
          </View>
          <View style={styles.geolocationCard}>
            <TouchableOpacity 
              style={styles.checkboxContainer}
              onPress={handleGeolocationToggle}
            >
              <View style={[
                styles.checkbox, 
                isGeolocationEnabled && styles.checkboxSelected
              ]}>
                <Animated.View
                  style={{
                    position: 'absolute',
                    transform: [{ scale: scaleAnim }],
                    opacity: opacityAnim,
                  }}
                >
                  <Icon 
                    name="check" 
                    color={theme.colors.white} 
                    size={theme.iconSize.extraSmall}
                  />
                </Animated.View>
              </View>
              <Text type="body" style={styles.geolocationText}>
                {t('registerFinalStep.geolocationText')}
              </Text>
            </TouchableOpacity>
          </View>

          <Button
            title={t('registerFinalStep.next')}
            containerStyle={styles.finalButton}
            onPress={() => {
              if (!profile?.accountId) return
              setUserID(profile?.accountId)
              navigate('Tab')
            }}
          />
        </OnBoarding>
      </ImageBackground>
    </Screen>
  )
}
